import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";
import { useUser } from "@clerk/clerk-react";
import { registerPush } from "../utils/push";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();

  useEffect(() => {
    if (clerkLoaded) {
      fetchUser();
    }
  }, [clerkLoaded, isSignedIn]);

  // Register push subscription only once per session or when identity changes
  const [lastSyncedId, setLastSyncedId] = useState(null);

  useEffect(() => {
    const currentId = user?._id || user?.id || "guest";
    if (lastSyncedId === currentId) return;

    (async () => {
      try {
        const token = localStorage.getItem("token");

        let guestId = localStorage.getItem("district_guest_id");
        if (!guestId) {
          guestId = "guest_" + Date.now() + Math.random().toString(36).substring(2, 9);
          localStorage.setItem("district_guest_id", guestId);
        }

        // Register push without forceRefresh to avoid infinite loop of re-subscriptions
        const res = await registerPush(token, {
          forceRefresh: false,
          guestId: !token ? guestId : undefined
        });

        if (res.success) {
          setLastSyncedId(currentId);
        }
      } catch (err) {
        console.warn("Push registration deferred:", err?.message || err);
      }
    })();
  }, [user, lastSyncedId]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await api.get("/auth/me");
      if (response.data && response.data.user) {
        setUser(response.data.user);
      } else if (response.data) {
        setUser(response.data);
      } else {
        // Handle case where /me returns null but user is signed in to Clerk
        if (isSignedIn) {
          console.log("🔄 Triggering Clerk profile sync...");
          const syncRes = await api.post("/auth/sync-clerk-user");
          setUser(syncRes.data.user);
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      console.error("Auth sync error:", error);
      // Try to sync if we're signed in but /me failed
      if (isSignedIn) {
        try {
          console.log("🔄 Attempting recovery sync...");
          const syncRes = await api.post("/auth/sync-clerk-user");
          setUser(syncRes.data.user);
        } catch (syncErr) {
          console.error("Recovery sync failed:", syncErr);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/auth/login", { email, password });
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Registration failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
